import { ProfileType, RecursivePartial, backendServices } from 'common';
import { CurrencyTypeUSD, FeedMessageType, InvoiceProfileInfinx } from '../../constants';
import { BuyerSignupFormData } from '../../pages/Buyer/buyersignup.types';
import { useGetBuyerCompanyUserProfile } from './useGetBuyerCompanyUserProfile';
import { useMemo } from 'react';

function convertViewModelToFormDefaults(
    viewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel>
): RecursivePartial<BuyerSignupFormData> {
    const form: RecursivePartial<BuyerSignupFormData> = {};

    form.generalInformation = {
        buyerCompanyAddress: {
            country: viewModel.Company?.Country,
        },
        currencyType: CurrencyTypeUSD,
    };

    const profileDeliveryMethod = viewModel.Profiles?.find(
        (profile) =>
            profile.Profile?.ProfileType === ProfileType.Invoice &&
            profile.Profile?.InvoiceProfileType !== InvoiceProfileInfinx
    );
    const transceptaSftp = profileDeliveryMethod?.FTPDownloads?.FTPDownload
        ? profileDeliveryMethod.FTPDownloads.FTPDownload
        : {}; // safe check of FTPDownload info

    form.inboundDataFeeds = {
        deliveryMethod: {
            useTransceptaSftp: true,
            useBuyerSftp: false,
            useEmail: false,
            sftpConnection: {
                server: transceptaSftp?.Server,
                downloadFolder: transceptaSftp?.DownloadFolder,
                uploadFolder: transceptaSftp?.UploadFolder,
                protocol: transceptaSftp?.Protocol,
                port: transceptaSftp?.Port?.toString(),
                // @ts-ignore
                timeoutInSeconds: transceptaSftp?.Timeout?.toString(),
            },
        },
    };

    return form;
}

export function useBuyerSignUpFormDefaults() {
    const { data: viewModelDefault, isLoading, error } = useGetBuyerCompanyUserProfile();

    let formDefaults: RecursivePartial<BuyerSignupFormData> = {};

    if (!isLoading && !error && viewModelDefault) {
        formDefaults = convertViewModelToFormDefaults(viewModelDefault);
    }

    return { formDefaults, viewModelDefault, isLoading, error };
}

export function useAutomatedFeedFormats(
    baseViewModel: RecursivePartial<backendServices.ViewModels.BuyerCompanyUserProfileViewModel>
) {
    const feedFormatOptions = useMemo(() => {
        const options: Record<FeedMessageType, Record<string, string>> = {
            [FeedMessageType.Vendor]: {},
            [FeedMessageType.User]: {},
            [FeedMessageType.GLAccount]: {},
            [FeedMessageType.PODelivery]: {},
            [FeedMessageType.POUpload]: {},
        };

        const invoiceProfileDeliveryMethod = baseViewModel.Profiles?.find(
            (profile) =>
                profile.Profile?.ProfileType === ProfileType.Invoice &&
                profile.Profile?.InvoiceProfileType !== InvoiceProfileInfinx
        );

        const messageRecognitions = invoiceProfileDeliveryMethod?.MessageRecognitions;

        messageRecognitions?.map((messageRecognition) => {
            // do not add message recognition to options without required fields
            if (
                !messageRecognition.Id ||
                !messageRecognition.MessageDataFormat_US ||
                !messageRecognition.MessageDataFormat
            ) {
                return;
            }

            // get all message recognitions and group them by type
            const messageIdString = messageRecognition.Id.toString();
            const messageDataFormat = messageRecognition.MessageDataFormat_US;

            switch (messageRecognition.MessageType) {
                case FeedMessageType.Vendor:
                    options[FeedMessageType.Vendor][messageDataFormat] = messageIdString;
                    break;
                case FeedMessageType.User:
                    options[FeedMessageType.User][messageDataFormat] = messageIdString;
                    break;
                case FeedMessageType.GLAccount:
                    options[FeedMessageType.GLAccount][messageDataFormat] = messageIdString;
                    break;
                case FeedMessageType.PODelivery:
                    options[FeedMessageType.PODelivery][messageDataFormat] = messageIdString;
                    break;
                case FeedMessageType.POUpload:
                    options[FeedMessageType.POUpload][messageDataFormat] = messageIdString;
                    break;
            }
        });

        return options;
    }, [baseViewModel]);

    return feedFormatOptions;
}
